import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { ExpertsDataService, ExpertsColumnsService } from '../../services';
import { DialogExpertFormComponent } from './expert-form/expert-form.component';
import { DialogExpertDetailsComponent } from './expert-details/expert-details.component';
import { Experts, ExpertsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-experts',
  templateUrl: './experts.component.html',
  styleUrls: ['./experts.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class ExpertsComponent implements OnInit, OnDestroy {

  private expertAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private expertEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private expertDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public expertToDelete: Experts = {};
  public experts: Experts[] = [];
  public expert: Experts = {};
  public selectedExperts: Experts[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private expertsDataService: ExpertsDataService,
    private expertsColumnsService: ExpertsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('ExpertsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Experts - Management');
    this.primengConfig.ripple = true;
    this.getExperts();
  }

  ngOnDestroy(): void {
    console.log('ExpertsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getExperts(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.expertsDataService.getExperts(this.parameters).subscribe((response: ExpertsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.experts = response.results;
        } else {
          this.experts = this.experts.concat(response.results);
        }
        if (response.inlineCount > this.experts.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getExperts();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('EXPERTSNOTLOADED')});
    });
  }

  importExperts(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.expertsDataService.addExperts(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('EXPERTSCREATED')});
                if (proceed === j) {
                  this.getExperts();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('EXPERTSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('EXPERTSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.expertsDataService.updateExperts(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('EXPERTSUPDATED')});
                if (proceed === j) {
                  this.getExperts();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('EXPERTSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('EXPERTSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.expertsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('experts.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedExperts() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Expertss?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.expertsDataService.deleteExperts(this.selectedExperts).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Expertss Deleted', life: 3000});
            this.getExperts();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Expertss not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Expertss not deleted'});
        });
        this.selectedExperts = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.experts, this.columns, 'Experts');
  }

  exportExcel() {
    ToolsService.exportExcel(this.experts, this.columns, 'Experts');
  }

  exportCsv() {
    ToolsService.exportCsv(this.experts, this.columns, 'Experts');
  }

  refresh() {
    this.expertsDataService.clearCache();
    this.getExperts();
  }

  expertAdd(): void {
    if (!this.navigation.getParamValue('experts.addInDialog', true)) {
      this.router.navigate(['/experts/add']);
      return;
    }
    this.expertAddDialog = this.dialogService.open(DialogExpertFormComponent, {
      header: '', // this.translate.instant('NEWexpert'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        expert: {id: null},
        users: this.expertsColumnsService.users,
      },
    });

    this.expertAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Experts added'});
        this.getExperts();
      }
    });
  }

  expertEdit(expert: Experts): void {
    if (!this.navigation.getParamValue('experts.editInDialog', true)) {
      this.router.navigate(['/experts/edit/' + expert.id]);
      return;
    }
    this.expertEditDialog = this.dialogService.open(DialogExpertFormComponent, {
      header: '', // this.translate.instant('EDITexpert') + ' ' + expert.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        expert: expert,
        users: this.expertsColumnsService.users,
      },
    });

    this.expertEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Expert updated'});
        this.getExperts();
      }
    });
  }

  expertDetails(expert: Experts): void {
    if (!this.navigation.getParamValue('experts.detailsInDialog', true)) {
      this.router.navigate(['/experts/details/' + expert.id]);
      return;
    }
    this.expertDetailsDialog = this.dialogService.open(DialogExpertDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        expert: expert,
      }
    });
  }

  expertDelete(expert: Experts): void {
    this.expertToDelete = expert;
    this.messageService.clear();
    this.messageService.add({
      key: 'expertDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + expert.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.expertsDataService.deleteExpert(this.expertToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Expert deleted'});
      this.getExperts();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Expert not deleted'});
    });
  }

  onDeleteReject(): void {
    this.expertToDelete = {};
    this.messageService.clear('expertDelete');
  }

}
